import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiBrain, FiX, FiSend, FiLoader } = FiIcons;

const AIAssistant = ({ show, onClose, question, answer, context }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const sendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    // Simulate AI response
    setTimeout(() => {
      const aiResponse = generateAIResponse(input, question, answer, context);
      setMessages(prev => [...prev, { role: 'assistant', content: aiResponse }]);
      setIsLoading(false);
    }, 1500);
  };

  const generateAIResponse = (userInput, question, answer, context) => {
    // Mock AI responses based on question type and context
    const responses = {
      equity: "For a 50/50 equity split, consider implementing a 4-year vesting schedule with a 1-year cliff. This protects both founders if one leaves early. The standard approach is that unvested shares return to the company.",
      
      payment: "For contractor agreements, I recommend milestone-based payments (e.g., 50% upfront, 50% on completion) for projects under $10k. For larger projects, consider 30% upfront, 40% at midpoint, 30% on completion.",
      
      ip: "It's crucial that all intellectual property created for the business is assigned to the company, not individual founders. This prevents disputes if someone leaves and ensures clean ownership for future investors.",
      
      termination: "Include both voluntary and involuntary termination clauses. For voluntary departure, typically 30 days notice is required. For cause termination, consider what constitutes 'cause' - breach of duties, criminal activity, etc.",
      
      default: "I can help you understand the implications of different choices here. What specific aspect would you like me to explain? I can provide insights on fairness, legal implications, or industry standards."
    };

    // Simple keyword matching for demo
    const lowerInput = userInput.toLowerCase();
    if (lowerInput.includes('equity') || lowerInput.includes('split')) return responses.equity;
    if (lowerInput.includes('payment') || lowerInput.includes('money')) return responses.payment;
    if (lowerInput.includes('ip') || lowerInput.includes('intellectual')) return responses.ip;
    if (lowerInput.includes('termination') || lowerInput.includes('leave')) return responses.termination;
    
    return responses.default;
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, x: 300 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 300 }}
          className="fixed right-4 top-24 bottom-4 w-96 glass-effect rounded-2xl shadow-2xl z-40 flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center">
                <SafeIcon icon={FiBrain} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">AI Assistant</h3>
                <p className="text-sm text-slate-600">Get smart suggestions</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-slate-500 hover:text-slate-700 transition-colors"
            >
              <SafeIcon icon={FiX} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-100 to-purple-100 flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiBrain} className="text-2xl text-blue-600" />
                </div>
                <p className="text-slate-600 mb-2">Hi! I'm here to help.</p>
                <p className="text-sm text-slate-500">Ask me about fairness, legal implications, or industry standards for this question.</p>
              </div>
            )}

            {messages.map((message, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-lg ${
                    message.role === 'user'
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-100 text-slate-800'
                  }`}
                >
                  <p className="text-sm">{message.content}</p>
                </div>
              </motion.div>
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-slate-100 p-3 rounded-lg">
                  <SafeIcon icon={FiLoader} className="animate-spin text-slate-600" />
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-6 border-t border-slate-200">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                placeholder="Ask me anything about this question..."
                className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || isLoading}
                className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                <SafeIcon icon={FiSend} className="text-sm" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AIAssistant;